import { createTheme } from '@mui/material/styles';

const PAPER   = '#F7F5EF';  // warm paper — page background
const SURFACE = '#EFEDE6';  // slightly warmer — card / paper surfaces
const INK     = '#252721';  // charcoal ink — primary text & headings
const STONE   = '#74736A';  // muted stone — secondary text & UI
const OLIVE   = '#74776A';  // subdued olive — accent
const RULE    = '#D8D5CC';  // thin border / divider

const theme = createTheme({
  cssVariables: true,
  typography: {
    fontFamily: "'Quicksand', sans-serif",
    h1: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontWeight: 300,
      letterSpacing: '0.02em',
    },
    h2: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontWeight: 300,
      letterSpacing: '0.02em',
    },
    h3: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontWeight: 400,
      letterSpacing: '0.01em',
    },
    h4: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontWeight: 400,
    },
    h5: {
      fontFamily: "'Quicksand', sans-serif",
      fontWeight: 600,
    },
    h6: {
      fontFamily: "'Quicksand', sans-serif",
      fontWeight: 500,
    },
    body1: {
      fontWeight: 400,
      lineHeight: 1.7,
    },
    body2: {
      fontWeight: 400,
    },
    button: {
      fontFamily: "'Quicksand', sans-serif",
      textTransform: 'none',
    },
  },
  palette: {
    mode: 'light',
    background: {
      default: PAPER,
      paper: SURFACE,
    },
    primary: {
      main: INK,
      contrastText: PAPER,
    },
    secondary: {
      main: STONE,
      contrastText: PAPER,
    },
    info: {
      main: OLIVE,
      contrastText: PAPER,
    },
    // warning and success are remapped to neutral stone so existing
    // page-level color references continue to render in theme tones
    warning: {
      main: STONE,
      contrastText: PAPER,
    },
    success: {
      main: OLIVE,
      contrastText: PAPER,
    },
    text: {
      primary: INK,
      secondary: STONE,
    },
    divider: RULE,
  },
  spacing: 8,
  shape: {
    borderRadius: 0,
  },
  // Flat editorial — no shadows
  shadows: Array(25).fill('none'),
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontFamily: "'Quicksand', sans-serif",
          fontWeight: 500,
          borderRadius: 0,
          padding: '8px 20px',
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 0,
          border: `1px solid ${RULE}`,
          transition: 'none',
          '&:hover': { transform: 'none', boxShadow: 'none' },
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { borderRadius: 0 },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 2,
          fontFamily: "'Quicksand', sans-serif",
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: RULE },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: { borderRadius: 0 },
      },
    },
  },
  custom: {
    dimensions: {
      profileImage: {
        width: 300,
        height: 300,
      },
      logo: {
        small: 40,
        medium: 60,
        large: 80,
      },
      icon: {
        small: 20,
        medium: 40,
        large: 60,
      },
    },
    borderRadius: {
      image: 0,
      card: 0,
      button: 0,
    },
    transitions: {
      default: 'none',
      transform: 'none',
      hover: 'none',
    },
    layout: {
      pageTopPadding: '8vh',
      sectionSpacing: 40,
      itemSpacing: 20,
      containerMaxWidth: 'md',
    },
  },
});

export default theme;
